// ============================================================
// Enums
// ============================================================

/** Flashcard type — mirrors Anki note types */
export enum FlashcardType {
  BASIC = 'basic',
  CLOZE = 'cloze',
  TYPE_IN = 'type-in',
  IMAGE_OCCLUSION = 'image-occlusion',
  MULTIPLE_CHOICE = 'multiple-choice',
}

/** Content format of a flashcard field / import-export format */
export enum FlashcardFormat {
  PLAIN = 'plain',
  JSON = 'json',
  MARKDOWN = 'markdown',
  HTML = 'html',
  ANKI = 'anki',
  CSV = 'csv',
  CLOZE = 'cloze',
  APKG = 'apkg',
}

/** How a flashcard was created */
export enum FlashcardSource {
  MANUAL = 'manual',
  IMPORT = 'import',
  SHARED = 'shared',
}

/** Deck source content format — used for Markdown / plaintext deck sources */
export enum SourceFormat {
  MARKDOWN = 'markdown',
  PLAINTEXT = 'plaintext',
}

/** Deck visibility level */
export enum SharingVisibility {
  PRIVATE = 'private',
  PUBLIC = 'public',
  SHARED_WITH = 'shared_with',
}

/** Rating given by the user during a review (SM-2 / FSRS) */
export enum CardRating {
  AGAIN = 'again',
  HARD = 'hard',
  GOOD = 'good',
  EASY = 'easy',
}

/** Spaced-repetition algorithm */
export enum SRAlgorithm {
  SM2 = 'sm2',
  FSRS = 'fsrs',
}

/** Current learning state of a card in the SR scheduler */
export enum SRState {
  NEW = 'new',
  LEARNING = 'learning',
  REVIEW = 'review',
  RELEARNING = 'relearning',
}

/** Study session type */
export enum SessionType {
  REVIEW = 'review',
  CRAM = 'cram',
  CUSTOM = 'custom',
  TEST = 'test',
}

/** Goal status lifecycle */
export enum GoalStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PAUSED = 'paused',
}

/** Goal target type */
export enum GoalType {
  DAILY_CARDS = 'daily-cards',
  DAILY_TIME = 'daily-time',
  DECK_COMPLETION = 'deck-completion',
  ACCURACY_TARGET = 'accuracy-target',
}

/** Sharing permission level for a deck */
export enum SharePermission {
  VIEW = 'view',
  STUDY = 'study',
  COPY = 'copy',
}

/** Notification type */
export enum NotificationType {
  GOAL_REMINDER = 'goal-reminder',
  GOAL_EXPIRED = 'goal-expired',
  SHARED_DECK = 'shared-deck',
  STUDY_REMINDER = 'study-reminder',
  ADMIN_MESSAGE = 'admin-message',
}

/** User role */
export enum UserRole {
  USER = 'user',
  CREATOR = 'creator',
  ADMIN = 'admin',
  ORGANIZATION_ADMIN = 'organization-admin',
  ORGANIZATION_MEMBER = 'organization-member',
}

/** Test / study mode */
export enum TestMode {
  STUDY = 'study',
  REVIEW = 'review',
  CRAM = 'cram',
  EXAM = 'exam',
}

/** Deck category */
export enum DeckCategory {
  LANGUAGES = 'languages',
  SCIENCE = 'science',
  MATH = 'math',
  HISTORY = 'history',
  MEDICINE = 'medicine',
  LAW = 'law',
  IT = 'it',
  ART = 'art',
  MUSIC = 'music',
  OTHER = 'other',
}

// ============================================================
// Lesson Enums
// ============================================================

/** Tipo di blocco di contenuto in una lezione */
export enum LessonBlockType {
  MARP_SLIDES = 'marp-slides',
  VIDEO = 'video',
  TEXT = 'text',
}

/** Provider video supportati nei blocchi di tipo VIDEO */
export enum VideoProvider {
  VDOCIPHER = 'vdocipher',
  BUNNY_STREAM = 'bunny-stream',
  YOUTUBE = 'youtube',
  VIMEO = 'vimeo',
  DIRECT = 'direct',
}

// ============================================================
// Content Versioning Enums
// ============================================================

/** Status of a content version in the versioning workflow */
export enum ContentStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  PUBLISHED = 'published',
}

// ============================================================
// Course Enums
// ============================================================

/** Course purchase status */
export enum PurchaseStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  REFUNDED = 'refunded',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

// ============================================================
// Audit Trail Enums
// ============================================================

/** Audit event types — used by AuditTrailService to categorize events */
export enum AuditEventType {
  // Organization events
  ORG_MEMBER_JOINED = 'member_joined',
  ORG_MEMBER_REMOVED = 'member_removed',
  ORG_MEMBER_DISABLED = 'member_disabled',
  ORG_MEMBER_ENABLED = 'member_enabled',
  // Course assignment events
  COURSE_ASSIGNED = 'course_assigned',
  COURSE_UNASSIGNED = 'course_unassigned',
  COURSE_BATCH_ASSIGNED = 'course_batch_assigned',
  // Content versioning events
  CONTENT_VERSION_PUBLISHED = 'content_version_published',
  CONTENT_GOVERNANCE_UPDATED = 'content_governance_updated',
  // Study events
  STUDY_SESSION_STARTED = 'study_session_started',
  STUDY_SESSION_COMPLETED = 'study_session_completed',
  LESSON_STARTED = 'lesson_started',
  LESSON_COMPLETED = 'lesson_completed',
  // Certificate events
  CERTIFICATE_GENERATED = 'certificate_generated',
  // Organization management events
  ORG_REGISTERED = 'org_registered',
  ORG_UPDATED = 'org_updated',
  // Course test events
  COURSE_TEST_STARTED = 'course_test_started',
  COURSE_TEST_COMPLETED = 'course_test_completed',
  COURSE_TEST_PASSED = 'course_test_passed',
  COURSE_TEST_FAILED = 'course_test_failed',
  // Admin management events
  ADMIN_USER_DISABLED = 'admin_user_disabled',
  ADMIN_USER_ENABLED = 'admin_user_enabled',
  ADMIN_ORG_DISABLED = 'admin_org_disabled',
  ADMIN_ORG_ENABLED = 'admin_org_enabled',
  ADMIN_NOTIFICATION_SENT = 'admin_notification_sent',
  // Assessment events
  ASSESSMENT_STARTED = 'assessment_started',
  ASSESSMENT_COMPLETED = 'assessment_completed',
  ASSESSMENT_NC_GENERATED = 'assessment_nc_generated',
  ASSESSMENT_NC_RESOLVED = 'assessment_nc_resolved',
}

// ============================================================
// Telemetry Enums
// ============================================================

/** Telemetry event type — categorizes client-side telemetry events */
export enum TelemetryEventType {
  /** Uncaught JavaScript error (window.onerror) */
  UI_ERROR = 'ui_error',
  /** Unhandled Promise rejection */
  UNHANDLED_REJECTION = 'unhandled_rejection',
  /** HTTP error (non-2xx response from axios interceptor) */
  HTTP_ERROR = 'http_error',
  /** Network error (no response received) */
  NETWORK_ERROR = 'network_error',
  /** Custom application event (video completed, feature used, etc.) */
  EVENT = 'event',
  /** Resource load error (script, image, link, font, etc. blocked or failed) */
  RESOURCE_ERROR = 'resource_error',
  /** Error captured from console.error() wrapper */
  CONSOLE_ERROR = 'console_error',
}

/** Telemetry severity level */
export enum TelemetrySeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

// ============================================================
// Assessment Enums
// ============================================================

/** Livello di compliance di una risposta/opzione */
export enum AssessmentComplianceLevel {
  CONFORME = 'conforme',
  NON_CONFORME = 'non_conforme',
  NON_APPLICABILE = 'non_applicabile',
}

/** Gravità di una non conformità */
export enum AssessmentSeverity {
  LIEVE = 'lieve',
  MEDIO = 'medio',
  GRAVE = 'grave',
}

/** Stato di un attempt di assessment */
export enum AssessmentAttemptStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ABANDONED = 'abandoned',
}

/** Stato di compliance complessivo del report */
export enum AssessmentComplianceStatus {
  CONFORME = 'conforme',
  NON_CONFORME = 'non_conforme',
  PARZIALMENTE_CONFORME = 'parzialmente_conforme',
  NON_APPLICABILE = 'non_applicabile',
}

/** Stato del ciclo di vita di una non conformità */
export enum NonConformityStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
}

export * from './lesson-progress.enum';